import { useCallback } from 'react';
import { AreaPageListing_IntegerRange, MapPageListing } from '@zoocasa/go-search';
import MapListingCard, { CompliancyDelegateType } from 'components/map-listing-card';
import styles from './style.module.scss';
import CloseIcon from 'components/icon/close-icon';
import { buildClassName } from 'utils/build-class-name';
import { useTranslation } from 'next-i18next';
import { TFunction } from 'i18next';
import { LOCALE_COMMON_PATHS, Namespaces } from 'types/locales';

//#region Constants
export const CALLOUT_SALE_TYPE = 'sale';
export const CALLOUT_RENT_TYPE = 'rent';
export const CALLOUT_SOLD_TYPE = 'sold';
export const CALLOUT_RENTED_TYPE = 'rented';
export const CALLOUT_EXPIRED_TYPE = 'expired';
export const CALLOUT_UNKNOWN_TYPE = 'unknown';
//#endregion

//#region Types
export type CalloutType = typeof CALLOUT_RENT_TYPE 
| typeof CALLOUT_SALE_TYPE 
| typeof CALLOUT_SOLD_TYPE
| typeof CALLOUT_RENTED_TYPE
| typeof CALLOUT_EXPIRED_TYPE
| typeof CALLOUT_UNKNOWN_TYPE;


export type FavouriteDelegate = {
  isFavourite(id: number): boolean;
  toggleFavourite(id: number): boolean;
  favouriteEventTracker?: () => void;
};

export type CalloutProps = {
  listings: MapPageListing[];
  type: CalloutType;
  maxHeight: string;
  compliancyDelegate: CompliancyDelegateType;
  favouriteDelegate: FavouriteDelegate;
  onCloseClick: () => void;
  onListingClick: (listing: MapPageListing) => void;
  onListingAddressClick: (listing: MapPageListing) => void;
  /**
   * Optional callback fired when a terms-of-use-required card is clicked.
   * See {@link MapListingCard}'s `onTermsRequiredClick` for why this is a
   * separate prop (callouts are rendered in standalone React roots without
   * the app-level ModalContext).
   */
  onTermsRequiredClick?: (listing: MapPageListing) => void;
  isExpTheme?: boolean;
  headerLabel?: string;
  /** Custom class passed to each MapListingCard. */
  cardClassName?: string;
};
//#endregion

//#region Helper Functions
export function getImageUrl(listing: MapPageListing) {
  const key = listing?.imageRootStorageKey?.trim();
  if (!key || key.length === 0) return '';
  if (key.startsWith('http')) return key;
  return `https://cdn0.zoocasa.com/${key}.jpg`;
}

export function getSqftLabel(squareFootage?: AreaPageListing_IntegerRange) {
  let sqftLabel = '';
  if (squareFootage) {
    const { gt, gte, lt, lte } = squareFootage;
    const min = gt || gte;
    const max = lt || lte;
    
    // Note: if max sqft is 499 always show 0-499, for anything else show min-max or just min or max when only one available
    const sqftRange = max === 499 ? '0-499' : [min, max].filter(item => item).join('–');
    const sqft = sqftRange?.trim()?.length ? sqftRange.trim() : 'N/A';

    // For complex ranges or N/A, don't translate - just append 'sqft'
    // The translation system expects a single numeric count, not ranges
    sqftLabel = `${sqft} sqft`;
  }
  return sqftLabel;
}

export function getHeaderLabel(type: CalloutType, count: number, t: TFunction) {
  // Use translations
  switch (type) {
    case CALLOUT_SALE_TYPE:
      return t(LOCALE_COMMON_PATHS.MAP_CALLOUT_FOR_SALE, { count, defaultValue: `${count} for sale` });
    case CALLOUT_RENT_TYPE:
      return t(LOCALE_COMMON_PATHS.MAP_CALLOUT_FOR_RENT, { count, defaultValue: `${count} for rent` });
    case CALLOUT_SOLD_TYPE:
      return t(LOCALE_COMMON_PATHS.MAP_CALLOUT_SOLD, { count, defaultValue: `${count} sold` });
    case CALLOUT_RENTED_TYPE:
      return t(LOCALE_COMMON_PATHS.MAP_CALLOUT_RENTED, { count, defaultValue: `${count} rented` });
    case CALLOUT_EXPIRED_TYPE:
      return t(LOCALE_COMMON_PATHS.MAP_CALLOUT_EXPIRED, { count, defaultValue: `${count} expired` });
    default:
      return `${count}`;
  }
}
//#endregion

const Callout = function({ listings, type, maxHeight, compliancyDelegate, favouriteDelegate, onCloseClick, onListingClick, onListingAddressClick, onTermsRequiredClick, isExpTheme, headerLabel, cardClassName }: CalloutProps) {
  const { t } = useTranslation(Namespaces.COMMON);  
  const handleFavouriteClicked = useCallback((id: number) => favouriteDelegate.toggleFavourite(id), [favouriteDelegate]);

  return (
    <>
      <div className={styles['callout-header']}>
        <p>{headerLabel ?? getHeaderLabel(type, listings.length, t)}</p>
        {!headerLabel && <span>
          &nbsp;{t(LOCALE_COMMON_PATHS.MAP_CALLOUT_SELECTED, { defaultValue: 'selected' })}
        </span>}
        <div className={buildClassName(styles['wrapper'], !isExpTheme && styles['zoocasa-theme'])}>
          <CloseIcon className={buildClassName(styles.close)} onClick={onCloseClick} />
        </div>
      </div>
      <div className={styles['callout-body']} style={{ maxHeight }}>
        {listings.map(listing => (
          <MapListingCard key={listing.id}
            id={listing.id} 
            listing={listing}
            type={compliancyDelegate.getMapListingCardType(listing)}
            showProviderLogo={compliancyDelegate.mustShowProviderLogo(listing)} 
            sqftLabel={getSqftLabel(listing.squareFootage)}
            imageUrl={getImageUrl(listing)} 
            isFavourite={favouriteDelegate.isFavourite(listing.id)}
            onFavouriteClick={handleFavouriteClicked}
            onListingClick={onListingClick}
            onListingAddressClick={
              compliancyDelegate.shouldOpenAsModal(listing) ? onListingAddressClick : undefined
            }
            onTermsRequiredClick={onTermsRequiredClick}
            isExpTheme={isExpTheme}
            hideSquareFootage={compliancyDelegate.hideSquareFootage(listing)}
            className={cardClassName}
          />
        ))}
      </div>
    </>
  );
};

export default Callout;